/**
 * Interface for dropdown column configuration
 */
export interface DropdownColumnConfig {
  columnNames: string[];
  values: string[];
}

/**
 * Interface for sheet dropdown configuration
 */
export interface SheetDropdownConfig {
  sheetName: string;
  dropdownColumns: { [key: string]: DropdownColumnConfig };
}

/**
 * Dropdown configuration for Contact Persons sheet
 */
export const CONTACTPERSONS_DROPDOWN_CONFIG: SheetDropdownConfig = {
  sheetName: 'contactpersons',
  dropdownColumns: {
    salutation: {
      columnNames: ['salutation', 'aanhef', 'title'],
      values: ['Mr', 'Ms', 'Mr and Ms']
    }
  }
};

/**
 * Dropdown configuration for Stakeholders sheet
 */
export const STAKEHOLDERS_DROPDOWN_CONFIG: SheetDropdownConfig = {
  sheetName: 'stakeholders',
  dropdownColumns: {
    interest: {
      columnNames: ['interest', 'interesse'],
      values: ['Unknown', 'None', 'Low', 'Medium', 'Elevated', 'High']
    },
    influence: {
      columnNames: ['influence', 'invloed'],
      values: ['Unknown', 'None', 'Low', 'Medium', 'Elevated', 'High']
    },
    tension: {
      columnNames: ['tension', 'spanning'],
      values: ['Unknown', 'None', 'Low', 'Medium', 'Elevated', 'High']
    },
    trust: {
      columnNames: ['trust', 'vertrouwen'],
      values: ['Unknown', 'None', 'Low', 'Medium', 'Elevated', 'High']
    }
  }
};

/**
 * Dropdown configuration for Requirements sheet
 */
export const REQUIREMENTS_DROPDOWN_CONFIG: SheetDropdownConfig = {
  sheetName: 'requirements',
  dropdownColumns: {
    requirement_status: {
      columnNames: ['requirement_status', 'status'],
      values: ['Initial', 'Ready for evaluation', 'Honored', 'Rejected', 'Expired', 'Next phase']
    },
    requirement_text_status: {
      columnNames: ['requirement_text_status', 'text_status'],
      values: ['Concept', 'Definitive']
    }
  }
};

/**
 * Dropdown configuration for Complaints sheet
 */
export const COMPLAINTS_DROPDOWN_CONFIG: SheetDropdownConfig = {
  sheetName: 'complaints',
  dropdownColumns: {
    status: {
      columnNames: ['status'],
      values: ['Open', 'Pending', 'Closed']
    }
  }
};

/**
 * Dropdown configuration for Issues sheet
 */
export const ISSUES_DROPDOWN_CONFIG: SheetDropdownConfig = {
  sheetName: 'issues',
  dropdownColumns: {
    status: {
      columnNames: ['status'],
      values: ['Open', 'Closed']
    },
    impact: {
      columnNames: ['impact'],
      values: ['Negligible', 'Low', 'Middle', 'High', 'Catastrophic']
    }
  }
};

/**
 * Dropdown configuration for Permits sheet
 */
export const PERMITS_DROPDOWN_CONFIG: SheetDropdownConfig = {
  sheetName: 'permits',
  dropdownColumns: {
    status: {
      columnNames: ['status'],
      values: ['Open', 'In progress', 'Rejected', 'Approved']
    }
  }
};

/**
 * Map of sheet names to their dropdown configurations
 */
export const SHEET_DROPDOWN_CONFIG_MAP: { [key: string]: SheetDropdownConfig } = {
  stakeholders: STAKEHOLDERS_DROPDOWN_CONFIG,
  contactpersons: CONTACTPERSONS_DROPDOWN_CONFIG,
  contact_persons: CONTACTPERSONS_DROPDOWN_CONFIG,
  contactpersonen: CONTACTPERSONS_DROPDOWN_CONFIG,
  requirements: REQUIREMENTS_DROPDOWN_CONFIG,
  klanteisen: REQUIREMENTS_DROPDOWN_CONFIG,
  complaints: COMPLAINTS_DROPDOWN_CONFIG,
  meldingen: COMPLAINTS_DROPDOWN_CONFIG,
  issues: ISSUES_DROPDOWN_CONFIG,
  permits: PERMITS_DROPDOWN_CONFIG,
  vergunningen: PERMITS_DROPDOWN_CONFIG,
};

/**
 * Dutch translations for dropdown values
 * English values are the keys; when language is 'nl' we return the Dutch value.
 */
export const DROPDOWN_TRANSLATIONS_NL: { [sheetKey: string]: { [configKey: string]: { [enValue: string]: string } } } = {
  contactpersons: {
    salutation: { 'Mr': 'De heer', 'Ms': 'Mevrouw', 'Mr and Ms': 'De heer en Mevrouw' }
  },
  contact_persons: {
    salutation: { 'Mr': 'De heer', 'Ms': 'Mevrouw', 'Mr and Ms': 'De heer en Mevrouw' }
  },
  contactpersonen: {
    salutation: { 'Mr': 'De heer', 'Ms': 'Mevrouw', 'Mr and Ms': 'De heer en Mevrouw' }
  },
  stakeholders: {
    interest: {
      'Unknown': 'Onbekend',
      'None': 'Geen',
      'Low': 'Laag',
      'Medium': 'Gemiddeld',
      'Elevated': 'Verhoogd',
      'High': 'Hoog'
    },
    influence: {
      'Unknown': 'Onbekend',
      'None': 'Geen',
      'Low': 'Laag',
      'Medium': 'Gemiddeld',
      'Elevated': 'Verhoogd',
      'High': 'Hoog'
    },
    tension: {
      'Unknown': 'Onbekend',
      'None': 'Geen',
      'Low': 'Laag',
      'Medium': 'Gemiddeld',
      'Elevated': 'Verhoogd',
      'High': 'Hoog'
    },
    trust: {
      'Unknown': 'Onbekend',
      'None': 'Geen',
      'Low': 'Laag',
      'Medium': 'Gemiddeld',
      'Elevated': 'Verhoogd',
      'High': 'Hoog'
    }
  },
  requirements: {
    requirement_status: {
      'Initial': 'Initieel',
      'Ready for evaluation': 'Klaar voor beoordeling',
      'Honored': 'Gehonoreerd',
      'Rejected': 'Afgewezen',
      'Expired': 'Verlopen',
      'Next phase': 'Volgende fase'
    },
    requirement_text_status: { 'Concept': 'Concept', 'Definitive': 'Definitief' }
  },
  klanteisen: {
    requirement_status: {
      'Initial': 'Initieel',
      'Ready for evaluation': 'Gereed voor honoreringsproces',
      'Honored': 'Gehonoreerd',
      'Rejected': 'Afgewezen',
      'Expired': 'Vervallen',
      'Next phase': 'Volgende fase'
    },
    requirement_text_status: { 'Concept': 'Concept', 'Definitive': 'Definitief' }
  },
  complaints: {
    status: { 'Open': 'Open', 'Pending': 'In behandeling', 'Closed': 'Gesloten' }
  },
  meldingen: {
    status: { 'Open': 'Open', 'Pending': 'In behandeling', 'Closed': 'Gesloten' }
  },
  issues: {
    status: { 'Open': 'Open', 'Closed': 'Gesloten' },
    impact: { 'Negligible': 'Verwaarloosbaar', 'Low': 'Laag', 'Middle': 'Midden', 'High': 'Hoog', 'Catastrophic': 'Catastrofaal' }
  },
  permits: {
    status: { 'Open': 'Open', 'In progress': 'In behandeling', 'Rejected': 'Afgewezen', 'Approved': 'Verleend' }
  },
  vergunningen: {
    status: { 'Open': 'Open', 'In progress': 'In behandeling', 'Rejected': 'Afgewezen', 'Approved': 'Verleend' }
  }
};

/**
 * Stakeholder text scale to backend numeric values.
 */
const STAKEHOLDER_SCALE_TO_BACKEND: { [label: string]: number | null } = {
  unknown: null,
  onbekend: null,
  none: 0,
  geen: 0,
  low: 25,
  laag: 25,
  medium: 50,
  gemiddeld: 50,
  elevated: 75,
  verhoogd: 75,
  high: 100,
  hoog: 100
};

/**
 * Gets the dropdown configuration for a given sheet name
 * @param sheetName - The name of the sheet
 * @returns The dropdown configuration object or null if no configuration found
 */
export function getSheetDropdownConfig(sheetName: string): SheetDropdownConfig | null {
  const key = sheetName.toLowerCase();
  return SHEET_DROPDOWN_CONFIG_MAP[key] || null;
}

/**
 * Gets dropdown values for a specific column in a sheet (translated when language is 'nl').
 * @param sheetName - The name of the sheet
 * @param columnHeader - The header name of the column
 * @param language - Current language ('nl' for Dutch, otherwise English)
 * @returns The dropdown values array (translated if nl) or null if no configuration found
 */
export function getDropdownValuesForColumn(sheetName: string, columnHeader: string, language?: string): string[] | null {
  const config = getSheetDropdownConfig(sheetName);
  if (!config) return null;

  // Strip required asterisk so "requirement_status*" / "impact*" match config columnNames
  const normalizedColumnHeader = columnHeader.replace(/\*/g, '').toLowerCase().trim();

  const useDutch = (language || '').toLowerCase() === 'nl';
  const sheetKey = sheetName.toLowerCase().trim();

  // Find matching dropdown column configuration
  for (const [configKey, dropdownConfig] of Object.entries(config.dropdownColumns)) {
    if (dropdownConfig.columnNames.includes(normalizedColumnHeader)) {
      const values = dropdownConfig.values;
      if (!useDutch) return values;

      const nlMap = DROPDOWN_TRANSLATIONS_NL[sheetKey]?.[configKey];
      if (!nlMap) return values;

      return values.map((enVal: string) => nlMap[enVal] ?? enVal);
    }
  }

  return null;
}

/**
 * Maps stakeholder scale labels (EN/NL) to backend expected values.
 * Returns original value for non-stakeholder columns/sheets or unknown labels.
 */
export function mapDropdownValueForBackend(sheetName: string, columnHeader: string, value: any): any {
  const sheetKey = (sheetName || '').toLowerCase().trim();
  const normalizedHeader = (columnHeader || '').toString().replace(/\*/g, '').toLowerCase().trim();

  if (sheetKey !== 'stakeholders') return value;
  if (!['interest', 'interesse', 'influence', 'invloed', 'tension', 'spanning', 'trust', 'vertrouwen'].includes(normalizedHeader)) {
    return value;
  }

  if (value === null || value === undefined || value === '') return value;

  const normalizedValue = value.toString().trim().toLowerCase();
  if (!(normalizedValue in STAKEHOLDER_SCALE_TO_BACKEND)) return value;

  const mappedValue = STAKEHOLDER_SCALE_TO_BACKEND[normalizedValue];
  if ((normalizedHeader === 'tension' || normalizedHeader === 'spanning') && mappedValue !== null) {
    return 100 - mappedValue;
  }

  return mappedValue;
}

/**
 * Checks if a column should have a dropdown for the given sheet
 * @param sheetName - The name of the sheet
 * @param columnHeader - The header name of the column
 * @returns True if the column should have a dropdown
 */
export function isDropdownColumn(sheetName: string, columnHeader: string): boolean {
  return getDropdownValuesForColumn(sheetName, columnHeader) !== null;
}

/**
 * Column header names that should show a calendar/date picker (any sheet).
 */
export const DATE_COLUMN_NAMES = ['date', 'datum', 'deadline', 'start_date', 'end_date'];

/**
 * Checks if a column is a date column (should show calendar dropdown).
 * @param columnHeader - The header name of the column (may include * for required)
 * @returns True if the column is a date column
 */
export function isDateColumn(columnHeader: string): boolean {
  const normalized = (columnHeader ?? '').toString().replace('*', '').trim().toLowerCase();
  return DATE_COLUMN_NAMES.includes(normalized);
}

export function isRequiredHeader(header: any): boolean {
  return typeof header === 'string' && header.includes('*');
}

export function normalize(value: any): string {
  return value === null || value === undefined ? '' : value.toString().trim();
}

export function isRowCompletelyEmpty(row: any[]): boolean {
  return row.every(cell =>
    cell === null ||
    cell === undefined ||
    cell.toString().trim() === ''
  );
}
